import { z } from 'zod';
import { type AuthState } from '../Constants';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import DataServices, {
  type ContentSummary,
  type PlaybacksHashMap,
} from '../dataServices/DataServices';
import Episode from '../dataServices/Episode';
import StandaloneChapter from '../dataServices/StandaloneChapter';
import { type IHistoryEntry } from '../dataServices/types';
import type PersonalizationServices from './index';

// TODO: [DTC-146] eventually move to DataServices
export type TProfileAPI = Pick<
  DataServices,
  | 'sessionId'
  | 'clearHistory'
  | 'addToHistory'
  | 'getHistory'
  | 'triggerPasswordResetEmail'
  | 'patchProfileData'
  | 'getProfileData'
  | 'getPlaybacks'
  | 'setPlayback'
  | 'setPlaybacks'
  | 'addFollows'
  | 'deleteFollow'
  | 'bulkUnfollow'
  | 'getFollows'
  | 'getLogoutUrl'
  | 'getQueue'
  | 'createAnonymousUser'
  | 'getIncompleteProfileData'
  | 'setQueue'
  | 'getAutoView'
  | 'getAdTargetingStatus'
  | 'setAdTargetingStatus'
  | 'getAdTargetingEligibility'
  | 'getPreferences'
  | 'setPreferences'
  | 'lastInteraction'
>;
export type TExperienceAPI = Pick<
  DataServices,
  'getContentSummaries' | 'getContentObject' | 'getContentObjects'
>;

/*** Follows ***/

export type IFollowsProvider = Pick<
  PersonalizationServices,
  'addFollows' | 'getFollows' | 'getFollowsWithSummaries' | 'deleteFollow' | 'bulkUnfollow'
>;

/*** Auth ***/

type IAuthState = {
  state: AuthState;
};

type IUnauthenticatedAuthState = {
  state: AuthState.NONE;
} & IAuthState;

type IAnonymousAuthState = {
  state: AuthState.ANON;
  userToken: string;
} & IAuthState;

type IAuthenticatedAuthState = {
  state: AuthState.AUTH;
  bearerHeader?: string;
  refreshToken: string;
  userToken: string;
} & IAuthState;

export type TAuthState = IUnauthenticatedAuthState | IAnonymousAuthState | IAuthenticatedAuthState;
export type TClientProvidedAuthState =
  | IUnauthenticatedAuthState
  | Pick<IAnonymousAuthState, 'state'>
  | (Pick<IAuthenticatedAuthState, 'state'> &
      (
        | {
            isTrusted: boolean;
            origin: string;
            data: Pick<IAuthenticatedAuthState, 'refreshToken'> & {
              authorizationHeader: string;
              userId: string;
            };
          }
        | (Pick<IAuthenticatedAuthState, 'refreshToken'> & {
            accessToken: string;
            userId: string;
          })
      ));

/*** Queue ***/

export type IQueueProvider = Pick<
  PersonalizationServices,
  | 'getQueue'
  | 'setQueue'
  | 'addToQueue'
  | 'removeFromQueue'
  | 'clearQueue'
  | 'addQueueListener'
  | 'removeQueueListener'
>;
export type IPlaybacksProvider = Pick<PersonalizationServices, 'getPlaybacks' | 'setPlayback'>;
export type IProfileProvider = Pick<
  PersonalizationServices,
  'getProfileData' | 'getIncompleteProfileData' | 'setProfileData'
>;

export type IPlayerSettingsProvider = Pick<
  PersonalizationServices,
  'getVolume' | 'setVolume' | 'getIsMuted' | 'setIsMuted' | 'getPlaybackRate' | 'setPlaybackRate'
>;

export type TQueueItem = Episode | StandaloneChapter;

export const isQueueItemIsEpisode = (item: unknown): item is Episode => {
  return item instanceof Episode;
};

export const isQueueItemStandaloneChapter = (item: unknown): item is StandaloneChapter => {
  return item instanceof StandaloneChapter;
};

export type IQueue = {
  currentId?: string;
  items: Array<TQueueItem>;
  itemsRemoved?: boolean;
};

export type TQueueListener = (queue: IQueue) => void;

/*** Playbacks ***/

export type TPlaybacksListener = (playbacks: PlaybacksHashMap) => void;

export type TPlayback = {
  contentId: string;
  playbackOffset: number;
};

/*** History ***/

export type IHistoryEntryWithSummary = {
  content: ContentSummary;
} & IHistoryEntry;

/*** Auto Home ***/

export type IAutoHomeWithSummary = {
  header: string;
  items: Array<IHistoryEntryWithSummary>;
  showLimit?: number;
  viewId: string;
  viewType: string;
  isLive?: boolean;
  moduleId: string;
};

/*** Profile ***/

export type TProfilePatch = Partial<
  Pick<
    IProfile,
    | 'userType'
    | 'dateOfBirth'
    | 'emailAddress'
    | 'firstName'
    | 'gender'
    | 'lastName'
    | 'notifications'
    | 'zipCode'
  >
>;

export enum Gender {
  FEMALE = 'FEMALE',
  MALE = 'MALE',
  NON_BINARY = 'NON_BINARY',
  NOT_SPECIFIED = 'PREFER_NOT_TO_SAY',
}

export enum Notifications {
  AUDACY_EMAIL = 'AUDACY_EMAIL',
  AUDACY_PUSH = 'AUDACY_PUSH',
}

export enum UserType {
  CONSUMER = 'CONSUMER',
  HOST = 'HOST',
}

export const IProfileSchema = z
  .object({
    dateOfBirth: z.string().optional(),
    emailAddress: z.string(),
    birthYear: z.number().optional(),
    firstName: z.string().optional(),
    gender: z.union([z.nativeEnum(Gender), z.literal("")]).optional(),
    lastLogin: z.string().optional(),
    lastName: z.string().optional(),
    notifications: z.array(z.any()).optional(),
    userType: z.any().optional(),
    zipCode: z.string().optional(),
  })
export type IProfile = z.infer<typeof IProfileSchema>;

export enum AdTargetingStatusParams {
  OPT_IN = 'OPT_IN',
  OPT_OUT = 'OPT_OUT',
}

export enum AdTargetingStatusResponse {
  OPT_IN = 'OPT_IN',
  OPT_OUT = 'OPT_OUT',
  INELIGIBLE = 'INELIGIBLE',
}

export type IAdTargeting = {
  adTargeting: AdTargetingStatusResponse;
};

export type IAdTargetingEligibility = {
  adTargeting: {
    eligible: boolean;
    canBeRequestedToOptIn: boolean;
  };
};

export enum AutoplayFrom {
  RESUME_POINT = 'RESUME_POINT',
  LIVE = 'LIVE',
}

export type IPreferences = {
  autoplay: boolean;
  autoplayFrom: AutoplayFrom;
  autoplayPromptEachTime: boolean;
  notifications: INotifications;
};

export type INotifications = {
  enabled?: boolean;
  newsPromo?: boolean;
  stationIds?: Array<string>;
  podcastIds?: Array<string>;
};

export type TPreferencesPatch = Partial<IPreferences>;
